import { createServerFn } from "@tanstack/react-start";
import { useSession } from "@tanstack/react-start/server";
import lodash from "lodash";
import * as client from "openid-client";

import { USER_SESSION_DURATION } from "@onesolution/constants";
import { logout } from "@onesolution/serverFn";
import { getKeycloakConfig } from "@onesolution/services";

import { cache } from "./cache";

export type SessionAuthData = {
	access_token: string;
	expires_in: number;
	refresh_token: string | undefined;
	code_verifier: string | undefined;
	state: string | undefined;
};

export type SessionUserData = {
	id_token: string | undefined;
	isAuthenticated: boolean;
	sub: string;
	email_verified: boolean;
	name: string;
	preferred_username: string;
	given_name: string;
	family_name: string;
	email: string;
	exp: number;
	iat: number;
	auth_time: number;
	jti: string;
	iss: string;
	aud: string | string[];
	typ: string;
	azp: string;
	sid: string;
	at_hash: string;
	roles: string[];
	acr: string;
	monday_workspace_id: string;
};

type SessionType = "auth" | "user";

type TargetSession<T extends SessionType> = T extends "auth"
	? SessionAuthData
	: T extends "user"
		? SessionUserData
		: never;

const AUTH_SESSION = {
	password: process.env.AUTH_SESSION_SECRET,
	name: "onesol-auth-session",
	cookie: {
		httpOnly: true,
		secure: import.meta.env.PROD,
		sameSite: "lax",
		maxAge: USER_SESSION_DURATION,
		path: "/",
	},
} as const;

const USER_SESSION = {
	password: process.env.USER_SESSION_SECRET,
	name: "onesol-user-session",
	cookie: {
		httpOnly: true,
		secure: import.meta.env.PROD,
		sameSite: "lax",
		maxAge: USER_SESSION_DURATION,
		path: "/",
	},
} as const;

const configMapper = {
	auth: AUTH_SESSION,
	user: USER_SESSION,
} as const;

async function getSessionByType<T extends SessionType>(type: T) {
	const config = configMapper[type];

	return await useSession<TargetSession<T>>(config);
}

async function getAuthSession() {
	return await getSessionByType("auth");
}

async function getUserSession() {
	return await getSessionByType("user");
}

async function refreshUserSession() {
	const authSession = await getAuthSession();
	const userSession = await getUserSession();

	if (!authSession.data.refresh_token) {
		console.error("No refresh token available");
		return await logout();
	}

	try {
		const config = await getKeycloakConfig();
		const tokens = await client.refreshTokenGrant(
			config,
			authSession.data.refresh_token,
			{
				scope: "openid profile email",
			},
		);
		const userDetails = tokens.claims();
		const newUserSessionInfo = {
			id_token: tokens.id_token,
			isAuthenticated: true,
			...userDetails,
		};

		await authSession.update({
			...authSession.data,
			access_token: tokens.access_token,
			refresh_token: tokens.refresh_token,
			expires_in: Date.now() + (tokens.expires_in || 0) * 1000,
		});

		await userSession.update(newUserSessionInfo);

		return newUserSessionInfo;
	} catch (error) {
		console.error("Token refresh failed:", error);
		return await logout();
	}
}

export const getAuthSessionData = createServerFn().handler(async () => {
	const authSession = await getAuthSession();

	return authSession.data;
});

export const updateAuthSession = createServerFn()
	.validator((data: Partial<SessionAuthData>) => data)
	.handler(async ({ data }) => {
		const authSession = await getAuthSession();

		await authSession.update({
			...authSession.data,
			...data,
		});
	});

export const getUserFromSession = createServerFn().handler(async () => {
	const authSession = await getAuthSession();
	const userSession = await getUserSession();

	if (!authSession.data.access_token || !userSession.data.isAuthenticated)
		return null;

	if (
		!lodash.isNil(authSession?.data?.expires_in) &&
		Date.now() > authSession?.data?.expires_in!
	) {
		await refreshUserSession();

		const userSession = await getUserSession();
		return userSession.data;
	}

	return userSession.data;
});

export const getUserSessionBypass = createServerFn().handler(async () => {
	const userSession = await getUserSession();

	if (!userSession.data.isAuthenticated) return null;

	return userSession.data;
});

export const updateUserSession = createServerFn()
	.validator((data: Partial<SessionUserData>) => data)
	.handler(async ({ data }) => {
		const userSession = await getUserSession();

		await userSession.update({
			...userSession.data,
			...data,
		});
	});

export const cleanSessions = createServerFn().handler(async () => {
	const authSession = await getAuthSession();
	const userSession = await getUserSession();

	cache.clear();
	await authSession.clear();
	await userSession.clear();
});
